import { Box, LinearProgress, Typography } from '@mui/material';
import { colorLinkBlue, colorDarkGrey } from 'customColors';
import { UploadIcon } from 'components/icons';
import { Button } from 'components/ui';
import React, { ReactElement } from 'react';
import { Image } from 'components/lib';
import { useSchoolsApi } from 'services/api';
import useSWR from 'swr';
import { useImageUpload } from 'hooks/useImageUpload';
import { useSnackbar } from 'notistack';

export const MAX_IMAGE_FILE_SIZE_MB = 5;

export interface DetailsTabProps {
  schoolId: string;
}

export const DetailsTab = ({ schoolId }: DetailsTabProps): ReactElement => {
  const schoolsApi = useSchoolsApi();

  const rSchool = useSWR(
    schoolId ? `/schools/${schoolId}` : null,
    async () => {
      if (!schoolId) return;

      const result = await schoolsApi.getSchool(schoolId);

      if (result.success) return result.value;

      if (result.error.response?.status === 401) return;

      throw new Error();
    },
    {
      revalidateOnFocus: false,
      refreshInterval: 0,
    },
  );

  const {
    open: openLogoSelector,
    url: logoUrl,
    progress: uploadLogoProgress,
  } = useImageUpload({ maxMb: 5 });

  const {
    open: openIconSelector,
    url: iconUrl,
    progress: uploadIconProgress,
  } = useImageUpload({ maxMb: 5 });

  const {
    open: openBannerSelector,
    url: bannerUrl,
    progress: uploadBannerProgress,
  } = useImageUpload({ maxMb: 10 });

  const { enqueueSnackbar } = useSnackbar();

  const handleSave = async () => {
    if (!schoolId) return;

    const result = await schoolsApi.saveSchool(schoolId, {
      iconUrl,
      logoUrl,
      bannerUrl,
    });

    if (!result.success) {
      enqueueSnackbar(result.error.message, { variant: 'error' });
      return;
    }

    enqueueSnackbar('Successfully saved school details', {
      variant: 'success',
    });

    await rSchool.mutate(result.value, false);
  };

  return (
    <Box maxWidth={618}>
      <Box mt="0px" mb="8px">
        <Typography sx={{ fontSize: '1.25em', fontWeight: 500 }}>
          Branding
        </Typography>
      </Box>

      <Box
        mb="32px"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-start' },
          gap: { xs: '16px', md: '32px' },
        }}
      >
        <Box
          sx={{
            width: { md: '360px' },
            maxWidth: { md: '300px' },
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 400, mb: '8px' }}>
            School Icon
          </Typography>
          <Typography sx={{ fontSize: '12px', color: colorDarkGrey }}>
            Recommended JPEG or PNG. Minimum 100X100 pixels. File size no larger
            than 10MB
          </Typography>
        </Box>
        <Box
          onClick={openIconSelector}
          sx={{
            mt: { md: '42px' },
            width: { xs: '100%', md: '72px' },
            height: { xs: '100px', md: '72px' },
            maxWidth: '100px',
            backgroundColor: 'neutral.main',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            position: 'relative',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: 'neutral.light',
            },
          }}
        >
          {iconUrl || rSchool.data?.iconUrl ? (
            <Image
              src={iconUrl ?? rSchool.data?.iconUrl ?? ''}
              width={72}
              height={72}
              alt="School Icon"
              style={{ borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <>
              <Box mb={4} sx={{ color: colorLinkBlue }}>
                <UploadIcon />
              </Box>
              <Typography
                sx={{
                  color: colorLinkBlue,
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '25px',
                  textDecorationLine: 'underline',
                }}
              >
                Upload
              </Typography>
            </>
          )}
          {uploadIconProgress && (
            <LinearProgress
              variant="determinate"
              value={uploadIconProgress}
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 8,
              }}
            />
          )}
        </Box>
      </Box>

      <Box
        mb="32px"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-start' },
          gap: { xs: '16px', md: '32px' },
        }}
      >
        <Box
          sx={{
            width: { md: '360px' },
            maxWidth: { md: '300px' },
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 400, mb: '8px' }}>
            School Logo
          </Typography>
          <Typography sx={{ fontSize: '12px', color: colorDarkGrey }}>
            Recommended JPEG or PNG. Minimum 250X100 pixels. File size no larger
            than 10MB
          </Typography>
        </Box>
        <Box
          onClick={openLogoSelector}
          sx={{
            mt: { md: '42px' },
            width: { xs: '300px', md: '250px' },
            height: '100px',
            backgroundColor: 'neutral.main',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            position: 'relative',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: 'neutral.light',
            },
          }}
        >
          {logoUrl || rSchool.data?.logoUrl ? (
            <Image
              src={logoUrl ?? rSchool.data?.logoUrl ?? ''}
              width={250}
              height={70}
              alt="School Logo"
              style={{ objectFit: 'contain' }}
            />
          ) : (
            <>
              <Box mb={4} sx={{ color: colorLinkBlue }}>
                <UploadIcon />
              </Box>
              <Typography
                sx={{
                  color: colorLinkBlue,
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '25px',
                  textDecorationLine: 'underline',
                }}
              >
                Upload
              </Typography>
            </>
          )}
          {uploadLogoProgress && (
            <LinearProgress
              variant="determinate"
              value={uploadLogoProgress}
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 8,
              }}
            />
          )}
        </Box>
      </Box>

      <Box
        mb="32px"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'flex-start' },
          gap: { xs: '16px', md: '32px' },
        }}
      >
        <Box
          sx={{
            width: { md: '360px' },
            maxWidth: { md: '300px' },
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontSize: '18px', fontWeight: 400, mb: '8px' }}>
            School Banner Image
          </Typography>
          <Typography sx={{ fontSize: '12px', color: colorDarkGrey }}>
            Recommended JPEG or PNG. Minimum 360X360 pixels. File size no larger
            than 10MB
          </Typography>
        </Box>
        <Box
          onClick={openBannerSelector}
          sx={{
            mt: { md: '42px' },
            width: { xs: '320px', md: '360px' },
            height: { xs: '310px', md: '360px' },
            backgroundColor: 'neutral.main',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
            '&:hover': {
              backgroundColor: 'neutral.light',
            },
          }}
        >
          {bannerUrl || rSchool.data?.bannerUrl ? (
            <Image
              src={bannerUrl ?? rSchool.data?.bannerUrl ?? ''}
              alt="School Banner"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <>
              <Box mb={8} sx={{ color: colorLinkBlue }}>
                <UploadIcon />
              </Box>
              <Typography
                sx={{
                  color: colorLinkBlue,
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '25px',
                  textDecorationLine: 'underline',
                }}
              >
                Upload
              </Typography>
            </>
          )}
          {uploadBannerProgress && (
            <LinearProgress
              variant="determinate"
              value={uploadBannerProgress}
              sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 8,
              }}
            />
          )}
        </Box>
      </Box>

      <Box mt="24px" mb="40px">
        <Button
          variant="vibrant"
          onClick={handleSave}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 320 }}
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};
